import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Cpu,
  Shield,
  Cloud,
  Database,
  GitBranch,
  Zap,
  Layers,
  LucideIcon,
} from "lucide-react";
import { HubNode } from "@/types/types";

interface NetworkHubProps {
  nodes: HubNode[];
  isGenerating: boolean;
  svgRef: any;
}

const iconMap: Record<string, LucideIcon> = {
  activity: Activity,
  cpu: Cpu,
  shield: Shield,
  cloud: Cloud,
  database: Database,
  "git-branch": GitBranch,
  zap: Zap,
  layers: Layers,
  "plug-cable": Layers,
  tool: Shield,
  repeat: GitBranch,
  settings: Cloud,
  book: Database,
};

const NetworkHub: React.FC<NetworkHubProps> = ({
  nodes,
  isGenerating,
  svgRef,
}) => {
  const width = 1000;
  const height = 1000;
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = 320;

  const nodePositions = useMemo(() => {
    return nodes.slice(0, 8).map((node: any, index: any) => {
      const angle = (index * (360 / 8) - 90) * (Math.PI / 180);
      return {
        ...node,
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
        angle: angle,
      };
    });
  }, [nodes, centerX, centerY, radius]);

  return (
    <div className="w-full h-full flex items-center justify-center">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-full max-w-200 max-h-200 drop-shadow-2xl"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Connection Lines */}
        {nodePositions.map((node: any, i: any) => (
          <g key={`line-${node.id}`}>
            <motion.path
              d={`M ${centerX} ${centerY} L ${node.x} ${node.y}`}
              stroke="#38bdf8"
              strokeWidth="2"
              strokeDasharray="4 8"
              strokeOpacity="0.4"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.4 }}
              transition={{ duration: 1, delay: i * 0.1, ease: "easeOut" }}
            />
            {/* Animated data particles */}
            <motion.circle
              r="3"
              fill="#7dd3fc"
              initial={{ offset: 0 }}
              animate={{ cx: [centerX, node.x], cy: [centerY, node.y] }}
              transition={{
                duration: 2 + Math.random(),
                repeat: Infinity,
                delay: i * 0.2,
                ease: "linear",
              }}
              filter="url(#glow)"
            />
          </g>
        ))}

        {/* Center Node with Logo */}
        <g className="cursor-pointer group">
          <motion.circle
            cx={centerX}
            cy={centerY}
            r="80"
            fill="#0f172a"
            stroke="#38bdf8"
            strokeWidth="2"
            animate={{
              scale: [1, 1.05, 1],
              strokeWidth: [2, 4, 2],
              opacity: [0.8, 1, 0.8],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          <circle
            cx={centerX}
            cy={centerY}
            r="65"
            fill="#1e293b"
            opacity="0.5"
          />

          {/* Logo Image */}
          <image
            href="/dark-logo.png"
            x={centerX - 40}
            y={centerY - 40}
            width="80"
            height="80"
          />

          {/* Rotating Outer Ring */}
          <motion.circle
            cx={centerX}
            cy={centerY}
            r="90"
            fill="none"
            stroke="#38bdf8"
            strokeWidth="1"
            strokeDasharray="10 20"
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            opacity="0.3"
          />
        </g>

        {/* Peripheral Nodes */}
        {nodePositions.map((node: any, i: any) => (
          <PeripheralNode
            key={node.id}
            node={node}
            index={i}
            isGenerating={isGenerating}
          />
        ))}
      </svg>
    </div>
  );
};

interface PeripheralNodeProps {
  node: HubNode & { x: number; y: number; angle: number };
  index: number;
  isGenerating: boolean;
}

const PeripheralNode: React.FC<PeripheralNodeProps> = ({
  node,
  index,
  isGenerating,
}) => {
  const Icon = iconMap[node.iconName] || Activity;

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
      whileHover={{ scale: 1.1 }}
      className="cursor-pointer group"
    >
      {/* Background Circle */}
      <circle
        cx={node.x}
        cy={node.y}
        r="45"
        fill="#0f172a"
        stroke="#334155"
        strokeWidth="1"
        className="group-hover:stroke-cyan-500 group-hover:fill-[#1e293b] transition-colors duration-300"
      />

      {/* Icon Wrapper */}
      <foreignObject x={node.x - 20} y={node.y - 20} width="40" height="40">
        <div className="flex items-center justify-center w-full h-full text-slate-400 group-hover:text-cyan-400 transition-colors">
          <Icon className="w-7 h-7" />
        </div>
      </foreignObject>

      {/* Label Box */}
      <g>
        <motion.rect
          initial={{ width: 0 }}
          whileHover={{ width: 160 }}
          x={node.x > 500 ? node.x + 55 : node.x - 215}
          y={node.y - 45}
          width="160"
          height="90"
          rx="10"
          fill="#1e293b"
          fillOpacity="0.85"
          stroke="#38bdf8"
          strokeWidth="1.2"
          strokeOpacity="0.6"
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        />

        {/* Persistent Title */}
        <text
          x={node.x}
          y={node.y + 72}
          textAnchor="middle"
          fill="#cbd5f5"
          className="text-[12px] uppercase font-bold tracking-widest group-hover:fill-cyan-400 transition-colors"
          style={{ fontSize: "12px" }}
        >
          {isGenerating ? "???" : node.title}
        </text>

        {/* Hover Detail Text */}
        <text
          x={node.x > 500 ? node.x + 70 : node.x - 205}
          y={node.y - 18}
          fill="#f8fafc"
          className="opacity-0 group-hover:opacity-100 pointer-events-none font-semibold"
          style={{ fontSize: "14px" }}
        >
          {node.title}
        </text>
        <foreignObject
          x={node.x > 500 ? node.x + 70 : node.x - 205}
          y={node.y - 2}
          width="140"
          height="55"
          className="opacity-0 group-hover:opacity-100 pointer-events-none"
        >
          <p className="text-[12px] text-slate-300 leading-snug">
            {node.description}
          </p>
        </foreignObject>
      </g>
    </motion.g>
  );
};

export default NetworkHub;
